// src/modules/messaging/attach-compose.ts — 2026-09-24
//
// Załączniki w małych czatach (karta zamówienia, szuflada sprawy) — ta sama umowa co centrum
// wiadomości (messaging/thread.ts): PDF/JPG/PNG/GIF/BMP/TIFF, max 5 MB, upload dwukrokowy przez
// /api/allegro/messaging/upload-attachment na KONCIE WĄTKU, potem `attachments:[{id}]` w wiadomości.
// Właściciel (24.09.2026, #112435): „ze strony zamówienia w wiadomościach nie widać załączników,
// nie można wysłać załącznika" — panel renderował sam tekst, a wysyłka szła zawsze z `null`.
import { _buildAttachmentHTML } from './bubble.ts';
import { zmniejszZdjecie, zmniejszZdjecia } from '../_zmniejsz-zdjecie.ts';

export const ALLEGRO_ATTACH_EXT = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'tif', 'tiff', 'pdf'];
export const ALLEGRO_ATTACH_MAX = 5 * 1024 * 1024;  // 5 MB (limit Allegro)
export const ALLEGRO_ATTACH_ACCEPT = '.png,.jpg,.jpeg,.gif,.bmp,.tif,.tiff,.pdf,image/png,image/jpeg,image/gif,image/bmp,image/tiff,application/pdf';
export const MAX_ZALACZNIKOW = 5;

/** Komunikat błędu walidacji albo null, gdy plik jest OK. */
export function bladZalacznika(file: { name?: string; size?: number }): string | null {
  const ext = String((file && file.name) || '').toLowerCase().split('.').pop() || '';
  if (ALLEGRO_ATTACH_EXT.indexOf(ext) === -1) {
    return 'Nieobsługiwany typ pliku. Allegro przyjmuje: PDF, JPG, PNG, GIF, BMP, TIFF (max 5 MB).';
  }
  if (Number((file && file.size) || 0) > ALLEGRO_ATTACH_MAX) {
    return 'Plik ' + String(file.name || '') + ' przekracza 5 MB — limit Allegro dla załączników.';
  }
  return null;
}

function _token(): string {
  try {
    return localStorage.getItem('sellbase_token') || localStorage.getItem('orderflow_token')
      || sessionStorage.getItem('sellbase_token') || sessionStorage.getItem('orderflow_token') || '';
  } catch (_e) {
    return '';
  }
}

/** Upload jednego pliku na konto wątku. Zwraca id załącznika do `attachments:[{id}]`. */
export async function uploadZalacznik(threadId: string, accountId: number | string | null, file: File): Promise<string> {
  // S-160 (MOB-7): zdjęcie z telefonu zmniejszone przed wysłaniem (_zmniejsz-zdjecie.ts; na laptopie bez zmian).
  file = await zmniejszZdjecie(file);
  const fd = new FormData();
  fd.append('file', file, file.name || 'attachment');
  const qs = 'thread_id=' + encodeURIComponent(String(threadId || ''))
    + (accountId ? '&account_id=' + encodeURIComponent(String(accountId)) : '');
  const token = _token();
  const resp = await fetch('/api/allegro/messaging/upload-attachment?' + qs, {
    method: 'POST',
    headers: token ? { Authorization: 'Bearer ' + token } : {},
    body: fd,
  });
  if (!resp.ok) {
    const err: any = await resp.json().catch(() => ({ detail: 'Upload failed' }));
    const det = err && err.detail;
    throw new Error(typeof det === 'string' ? det : ((det && (det.message || det.detail)) || 'Nie udało się wgrać załącznika'));
  }
  const data: any = await resp.json();
  const id = data && (data.id || (data.attachment && data.attachment.id));
  if (!id) throw new Error('Upload OK, ale brak ID załącznika');
  return String(id);
}

/** HTML załączników wiadomości (miniatura / link / „Allegro weryfikuje plik” / odrzucony). */
export function htmlZalacznikow(m: any, accountId: number | string | null): string {
  const atts = (m && Array.isArray(m.attachments)) ? m.attachments : [];
  if (!atts.length) return '';
  return '<div class="oc-chat-msg-att">' + atts.map((a: any) => _buildAttachmentHTML(a || {}, accountId)).join('') + '</div>';
}

export interface WyborZalacznikow {
  pliki(): File[];
  wyczysc(): void;
}

/** Pole wyboru plików + chipy z „×”. Walidacja typu/rozmiaru od razu, bez wysyłki. */
export function zamontujWyborZalacznikow(inputEl: HTMLInputElement, chipsEl: HTMLElement): WyborZalacznikow {
  let pliki: File[] = [];
  const toast = (msg: string, typ: string): void => {
    try { const f = (window as any).showToast; if (typeof f === 'function') f(msg, typ); } catch (_e) { /* noop */ }
  };
  const esc = (s: string): string => String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' } as any)[c]);
  const render = (): void => {
    chipsEl.innerHTML = pliki.map((f, i) =>
      '<span class="oc-chat-att-chip" title="' + esc(f.name) + '">' +
        '<i data-lucide="paperclip" style="width:11px;height:11px"></i><span>' + esc(f.name) + '</span>' +
        '<button type="button" data-att-remove="' + i + '" aria-label="Usuń załącznik">×</button>' +
      '</span>').join('');
    try { (window as any).lucide && (window as any).lucide.createIcons({ nodes: [chipsEl] }); } catch (_e) { /* noop */ }
  };
  chipsEl.addEventListener('click', (e: Event) => {
    const btn = (e.target as HTMLElement | null)?.closest('[data-att-remove]') as HTMLElement | null;
    if (!btn) return;
    const i = parseInt(btn.getAttribute('data-att-remove') || '-1', 10);
    if (i >= 0) pliki.splice(i, 1);
    render();
  });
  inputEl.addEventListener('change', async () => {
    const wybrane = Array.from(inputEl.files || []);
    inputEl.value = '';
    // Przegląd przed PROD (K-2, S-160): zdjęcie z telefonu zmniejszone PRZED sprawdzeniem limitu (6–15 MB z aparatu mieści się po zmniejszeniu); laptop = oryginał.
    const nowe = await zmniejszZdjecia(wybrane);
    for (const f of nowe) {
      const blad = bladZalacznika(f);
      if (blad) { toast(blad, 'warning'); continue; }
      if (pliki.length >= MAX_ZALACZNIKOW) { toast('Maksymalnie ' + MAX_ZALACZNIKOW + ' załączników w jednej wiadomości.', 'warning'); break; }
      pliki.push(f);
    }
    render();
  });
  return { pliki: () => pliki.slice(), wyczysc: () => { pliki = []; render(); } };
}
